import { fetchVerification } from '@afterpaytouch/event-verify'
import { VERIFY_SERVICE_BASE_URL, countrySelector, verifyServiceApi, emailSelector, registrationSelector, tokenSelector } from '../'
import {
  AddEmailToRegistrationRequest,
  AddMobileToRegistrationRequest,
  ConfirmRegistrationRequest,
  CreateRegistration,
  CreateRegistrationRequest,
  CreateRegistrationResponse,
  EmailRegistrationVerificationRequest,
  FinaliseAccountRequest,
  MobileRegistrationVerificationRequest,
  RegistrationData,
  RegistrationVerificationResponse,
  TransformedCreateRegistrationResponse,
  VerifyRegistrationAuthorizationRequest,
  VerifyRegistrationMobileEventRequest,
  VerifyRegistrationSMSAuthorizationResponse,
} from '../../model'
import { portalAction, PortalThunk } from '../types'
import { Country } from '@afterpay/types'

export const createRegistration: PortalThunk<CreateRegistration, TransformedCreateRegistrationResponse> = portalAction(
  'registration/new',
  async ({ flow, locale }: CreateRegistration, thunk) => {
    const email = emailSelector(thunk.getState())

    /*
      This is a temporary (hopefully) workaround for the purposes of email verification.
      Adding this header ensures that the email verification process will only run when the email is used to being registration rather than mobile.
     */
    const headers = {
      'X-Registration-Flow': flow,
    }
    const res = await thunk.extra
      .portalApi()
      .post<CreateRegistrationRequest, CreateRegistrationResponse>('/portal/consumers/registrations', { email, preferredLocale: locale }, headers)
    return { ...res, email }
  }
)

export const verifyRegistrationMobile: PortalThunk<void, RegistrationVerificationResponse> = portalAction('registration/verify/mobile', async (_, thunk) => {
  const { id: registrationId, data } = registrationSelector(thunk.getState())
  // @ts-expect-error: OPERATION BLEED STOPPER
  const { mobile, preferredLocale, ...consumerProfile } = data
  const transactionToken = tokenSelector(thunk.getState())
  const countryCode = countrySelector(thunk.getState())
  const amplitudeSessionId = (await import('amplitude-js')).default.getInstance()?.getSessionId() // TODO: is this ok?
  const request: MobileRegistrationVerificationRequest = {
    // @ts-expect-error: OPERATION BLEED STOPPER
    registrationId,
    transactionToken,
    countryCode,
    amplitudeSessionId,
    mobile,
    preferredLocale,
    consumerProfile,
  }
  return await thunk.extra.portalApi().post('/portal/consumers/registrations/sms-verification', request)
})

export const verifyRegistrationMobileSMSEvent: PortalThunk<VerifyRegistrationMobileEventRequest, void> = portalAction(
  'registration/verify/mobile/sms/delay',
  async (verifyRegistrationMobileEventRequest, thunk) => {
    const transactionToken = tokenSelector(thunk.getState())
    const { onSuccess, onWaiting, onError } = verifyRegistrationMobileEventRequest
    return await fetchVerification({
      url: encodeURI(`${VERIFY_SERVICE_BASE_URL}/sms/${transactionToken}`),
      onSuccess: () => {
        onSuccess()
      },
      onError: (d) => {
        onError(d)
      },
      onWaiting: (d) => {
        onWaiting(d)
      },
    })
  }
)

export const verifyRegistrationSMSAuthorization: PortalThunk<void, VerifyRegistrationSMSAuthorizationResponse> = portalAction(
  'registration/verify/mobile/sms/authz',
  async (_, thunk) => {
    const { id: registrationId, data } = registrationSelector(thunk.getState())
    const transactionToken = tokenSelector(thunk.getState())
    const amplitudeSessionId = (await import('amplitude-js')).default.getInstance()?.getSessionId()

    const request: VerifyRegistrationAuthorizationRequest = {
      registrationId: registrationId ?? '',
      transactionToken,
      amplitudeSessionId,
      consumer: data as RegistrationData,
    }
    const result = await verifyServiceApi
      .post<VerifyRegistrationAuthorizationRequest, VerifyRegistrationSMSAuthorizationResponse>('/authz/sms', request)
      .catch((e) => {
        return {
          code: e.response.data.code,
          message: e.response.data.message,
        }
      })
    return result
  }
)

export const verifyRegistrationEmail: PortalThunk<void, RegistrationVerificationResponse> = portalAction('registration/verify/email', async (_, thunk) => {
  const email = emailSelector(thunk.getState())
  const { id: registrationId } = registrationSelector(thunk.getState())
  const request: EmailRegistrationVerificationRequest = {
    // @ts-expect-error: OPERATION BLEED STOPPER
    registrationId,
    email,
  }
  return await thunk.extra.portalApi().post('/portal/consumers/registrations/email-verification', request)
})

export const confirmRegistrationMobile: PortalThunk<string, void> = portalAction('registration/mobile', async (smsCode, thunk) => {
  const { id: registrationId, data } = registrationSelector(thunk.getState())
  // @ts-expect-error: OPERATION BLEED STOPPER
  const { mobile } = data
  const request: AddMobileToRegistrationRequest = {
    // @ts-expect-error: OPERATION BLEED STOPPER
    registrationId,
    mobile,
    smsCode,
  }
  return await thunk.extra.portalApi().post('/portal/consumers/registrations/mobile', request)
})

export const confirmRegistrationEmail: PortalThunk<string, void> = portalAction('registration/email', async (emailCode, thunk) => {
  const email = emailSelector(thunk.getState())
  const { id: registrationId } = registrationSelector(thunk.getState())
  const request: AddEmailToRegistrationRequest = {
    // @ts-expect-error: OPERATION BLEED STOPPER
    registrationId,
    email,
    emailCode,
  }
  return await thunk.extra.portalApi().post('/portal/consumers/registrations/email', request)
})

export const confirmRegistration: PortalThunk<void, void> = portalAction('registration/confirm', async (_, thunk) => {
  const { id: registrationId, data } = registrationSelector(thunk.getState())
  // @ts-expect-error: OPERATION BLEED STOPPER
  const { preferredLocale, marketingOptIn } = data
  const request: ConfirmRegistrationRequest = {
    // @ts-expect-error: OPERATION BLEED STOPPER
    registrationId,
    origin: 'CHECKOUT',
    preferredLocale,
    ...(marketingOptIn !== null ? { marketingPreference: marketingOptIn } : null),
  }
  return await thunk.extra.portalApi().post('/portal/consumers/registrations/confirm', request)
})

export const finaliseAccountDetails: PortalThunk<void, void> = portalAction('registration/finaliseAccount', async (_req, thunk) => {
  const { data } = registrationSelector(thunk.getState())
  // @ts-expect-error: OPERATION BLEED STOPPER
  const { preferredLocale: _, ...finaliseAccountData } = data
  const request: FinaliseAccountRequest = {
    ...finaliseAccountData,
    countryCode: finaliseAccountData.countryCode as Country,
    creditPolicyAgree: false,
    providedCreditCheckConsent: finaliseAccountData.countryCode === Country.NZ,
  }
  return await thunk.extra.portalApi().post('/portal/consumers/account/account-details', request)
})

export const captureCashConsent: PortalThunk<void, void> = portalAction('registration/captureCashConsent', async (_, thunk) => {
  return await thunk.extra.portalApi().post<Record<string, never>, void>('/portal/consumers/account/cash-consent', {})
})
